/**
 * Tool-use row — Codex-style inline prose.
 *
 * Each tool call renders as a single inline row in the same column
 * and font as agent narration, sitting one notch quieter than
 * surrounding text so a long sequence of calls reads as a dim
 * ledger inside the conversation:
 *
 *   I'll patch the close path on the bash output handler.
 *
 *   Read transport.ts
 *   Edited transport.ts +5 −3
 *   Re-run the rome transport tests in the background
 *
 *   The handler now drains the close queue before…
 *
 * Every row leads with a **per-tool glyph** (Read = file, Bash =
 * terminal, Monitor = radio, TodoWrite = list-checks, etc.) and a
 * **bold verb** — usually the family verb ("Read", "Edited",
 * "Monitor"), but lifted from the tool's own ``description`` when
 * it starts with an imperative ("Re-run", "Boot") so the row
 * never doubles ("Ran Re-run …"). The glyph picks up the same
 * accent color as the verb via ``currentColor`` so the pair reads
 * as one unit, and both sit one notch lighter than the previous
 * pass (default Lucide stroke + ``font-medium``) so the row
 * scans without printing as jet-black on a dim line. Then a
 * short primary (basename / query / phrase), an optional dim
 * ``prose`` continuation, and a tiny diff badge where it earns
 * its keep. No card border, no background at rest. Hovering
 * reveals a faint neutral bleed.
 *
 * Pending tools trail a small spinner so the user can see the
 * call is still in flight. Errors swap the row's text to red but
 * still stay collapsed by default.
 *
 * Clicking expands the row in place. The expanded body sits flush
 * under the row, indented to the row's left edge and capped at
 * ~80% of the chat column so it never spans corner-to-corner. The
 * wrapper is otherwise neutral chrome — no family-colored left
 * rule, no card border around the column — and only escalates to a
 * subtle red rule when the tool errored. Full path / full command /
 * raw input live in the dropdown — never inline — so the column
 * stays scannable.
 *
 * Background shell / subagent meta renders as a compact continuation
 * box below the row, with a small latest-output tail embedded inside
 * the same box rather than a separate expanded transcript panel.
 */

import { AlertCircle, ChevronDown, ChevronRight, Square } from 'lucide-react';
import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import type { ActiveTask, SubagentState } from '@/lib/agent/reducer';
import type { ServerToolUseBlock, ToolResultBlock, ToolUseBlock } from '@/lib/agent/types';
import { codePanePathCandidates, notifyCodePaneFileChanged, openCodePaneFile } from '@/lib/notebook/codePaneSync';
import { cn } from '@/lib/utils/cn';
import { filePathForTool, hasToolBody, MediaThumb, renderToolBody } from './body';
import { extractMediaPieces, humanizeToolName, toolResultText } from './helpers';
import { useProjectCwd } from './ProjectCwdContext';
import { buildToolSummary, formatSubagentType } from './summary';
import { type TaskOutputReader, TaskTailView } from './TaskTailView';
import { toolFamily } from './tone';

export interface ToolUseBlockViewProps {
  block: ToolUseBlock | ServerToolUseBlock;
  result?: ToolResultBlock;
  /** Live subagent state for ``Task`` tool calls. */
  subagent?: SubagentState;
  /** Live background task state for tools that spawned SDK tasks. */
  activeTask?: ActiveTask;
  /** Current agent session id, used to tail active task output. */
  sessionId?: string | null;
  /** Stop a live background task from its inline tail. */
  onStopTask?: (taskId: string) => void;
  /** Test/story override for reading task output. Defaults to the workspace API. */
  taskOutputReader?: TaskOutputReader;
  /** Test/story override for task output polling cadence. */
  taskOutputPollIntervalMs?: number;
  /**
   * Whether this tool call is still live. When omitted, the view
   * falls back to its old local heuristic for stories / isolated
   * renders.
   */
  pending?: boolean;
  /** Partial JSON string when ``block.input`` has not yet been finalized. */
  partialJson?: string;
  /** Optional inline content rendered inside the expanded body. */
  children?: React.ReactNode;
  /** When true, force the expanded body to render even if the user hasn't toggled it. */
  forceExpanded?: boolean;
  /**
   * Notify the owning scroll container that the row's effective
   * height changed outside the parent commit -- specifically when
   * the expandable body opens / closes and when an active task
   * row (TaskRunLine + TaskTailView, ~150-200px) first attaches.
   * Without this synchronous call the chat relies on the RO alone,
   * which can lag the CSS transition by a frame and leaves the
   * reader detached from the floor while the subagent feed mounts.
   */
  onLayoutShift?: () => void;
  className?: string;
}

const FILE_MUTATION_TOOLS = new Set([
  'Write',
  'WriteFile',
  'Edit',
  'EditFile',
  'MultiEdit',
  'MultiEditFile',
  'NotebookEdit',
]);
const INSPECTABLE_FILE_TOOLS = new Set([
  'Read',
  'ReadFile',
  'Write',
  'WriteFile',
  'Edit',
  'EditFile',
  'MultiEdit',
  'MultiEditFile',
  'NotebookEdit',
]);

export const ToolUseBlockView = React.memo(function ToolUseBlockView({
  block: rawBlock,
  result,
  subagent,
  activeTask,
  sessionId,
  onStopTask,
  taskOutputReader,
  taskOutputPollIntervalMs,
  pending,
  partialJson,
  children,
  forceExpanded,
  onLayoutShift,
  className,
}: ToolUseBlockViewProps) {
  // Normalize ``ServerToolUseBlock`` to ``ToolUseBlock`` once per block
  // identity. ``BlockRouter`` passes the block by reference (no per-render
  // allocation), so this memo only re-runs when the underlying block
  // actually changes -- keeping the rest of the component on a stable
  // ``ToolUseBlock`` while preserving the ``React.memo`` skip on stable rows.
  const block = React.useMemo<ToolUseBlock>(
    () => (rawBlock.block_type === 'ToolUseBlock' ? rawBlock : { ...rawBlock, block_type: 'ToolUseBlock' }),
    [rawBlock]
  );
  const isSubagentTool = block.name === 'Task' || block.name === 'Agent';

  const cwd = useProjectCwd();
  const family = toolFamily(block.name);
  const input = block.input ?? {};
  const partialInput = React.useMemo(() => readPartialToolInput(partialJson), [partialJson]);
  const hasInputFields = Object.keys(input).length > 0;
  const taskInput = React.useMemo(() => inputFromActiveTask(activeTask), [activeTask]);
  const displayBlock = hasInputFields ? block : { ...block, input: { ...taskInput, ...partialInput } };
  const hasDisplayInputFields = Object.keys(displayBlock.input ?? {}).length > 0;
  // A completed call always describes itself via ``buildToolSummary``,
  // even with empty input -- otherwise a no-arg tool like ``TaskList``
  // stays stuck on the "Preparing …" pending label after its result
  // has already landed.
  const summary =
    hasInputFields || hasDisplayInputFields || result !== undefined
      ? buildToolSummary(displayBlock, cwd)
      : buildPendingToolSummary(displayBlock, cwd);
  // ``ExitPlanMode`` can land as ``is_error: true`` with content
  // ``"Exit plan mode?"`` when the user rejects a proposed plan.
  // That is an expected "user did not approve" signal, not a real
  // tool failure, so we exclude it from error styling.
  const isExitPlanMode = block.name === 'ExitPlanMode';
  const resultIsError = !isExitPlanMode && result?.is_error === true;
  const resultMessage = resultIsError ? (toolResultText(result) ?? '').trim() : '';
  const isInterrupted = resultIsError && resultMessage === 'Stopped by user';
  const isError = !isInterrupted && (resultIsError || subagent?.status === 'failed');
  const bodyIsError = isError || isInterrupted;
  const errorMessage = bodyIsError
    ? isInterrupted
      ? 'Stopped by user'
      : resultIsError
        ? resultMessage || 'Tool failed without a text error message'
        : (subagent?.summary ?? 'Subagent failed')
    : null;
  const mediaPieces = result ? extractMediaPieces(result) : [];

  const activeTaskForRunLine = taskRunLineActiveTask(activeTask, displayBlock.input ?? {});
  const taskTailOwnsHistory = activeTaskForRunLine !== undefined;
  const activeTaskIsRunning = activeTaskForRunLine !== undefined;
  const hasImageMedia = mediaPieces.some((piece) => piece.kind === 'image');
  const taskDefaultExpanded =
    activeTaskIsRunning && (activeTaskForRunLine?.category === 'bash' || activeTaskForRunLine?.category === 'agent');
  const shouldDefaultExpand = shouldDefaultExpandToolUseRow({
    taskDefaultExpanded,
    bodyIsError,
    hasImageMedia,
    toolName: block.name,
  });
  const hasBody = !taskTailOwnsHistory && hasToolBody({ block: displayBlock, result, isError: bodyIsError });
  const expandable = taskTailOwnsHistory || hasBody || !!children || mediaPieces.length > 0;
  const [expandedOverride, setExpandedOverride] = React.useState<boolean | null>(null);
  const isExpanded = !!forceExpanded || (expandedOverride ?? shouldDefaultExpand);
  const [bodyMounted, setBodyMounted] = React.useState(isExpanded);
  const shouldRenderBody = isExpanded || bodyMounted;
  const renderedBodyContent =
    shouldRenderBody && hasBody ? renderToolBody({ block: displayBlock, result, isError: bodyIsError }) : null;
  const inspectableTarget =
    !bodyIsError && INSPECTABLE_FILE_TOOLS.has(block.name)
      ? inspectableCodeTarget(filePathForTool(displayBlock.input ?? {}), cwd)
      : null;

  // In the full chat, pending comes from the reducer's live task /
  // awaiting state. Isolated stories omit that prop and keep the
  // older local fallback so "no result yet" examples still render
  // with the spinner. Background tasks can have a completed
  // ``tool_result`` for "started in background" while the SDK task is
  // still active; keep the row spinner live until that task finishes.
  const isPending = isToolUseRowPending(activeTaskForRunLine, pending, subagent, result);
  const notifiedFileChangeRef = React.useRef<string | null>(null);
  const wasPendingRef = React.useRef(isPending);
  const hadActiveRunLineRef = React.useRef(activeTaskForRunLine !== undefined);

  React.useEffect(() => {
    if (forceExpanded) setExpandedOverride(true);
  }, [forceExpanded]);

  React.useEffect(() => {
    if (isExpanded) {
      setBodyMounted(true);
      return undefined;
    }
    const timer = setTimeout(() => setBodyMounted(false), 220);
    return () => clearTimeout(timer);
  }, [isExpanded]);

  // Notify the chat synchronously whenever the row's effective
  // height changes outside the parent's commit -- the body
  // expanding / collapsing (200ms grid transition) and an active
  // task row (TaskRunLine + TaskTailView, ~150-200px) attaching
  // or detaching inside it. The RO catches these too, but it can
  // lag a frame at the start of the grid transition and the
  // reader visibly detaches from the floor before it snaps. The
  // useLayoutEffect lands the pin in the same commit as the
  // state change. ``snapToBottomIfFollowing`` itself is a no-op
  // when the reader isn't following the tail, so firing
  // unconditionally is safe.
  // biome-ignore lint/correctness/useExhaustiveDependencies: isExpanded and taskTailOwnsHistory are trigger-only height signals.
  React.useLayoutEffect(() => {
    onLayoutShift?.();
  }, [onLayoutShift, isExpanded, taskTailOwnsHistory]);

  React.useEffect(() => {
    const hasActiveRunLine = activeTaskForRunLine !== undefined;
    if (hadActiveRunLineRef.current && !hasActiveRunLine) {
      setExpandedOverride(false);
    }
    hadActiveRunLineRef.current = hasActiveRunLine;
  }, [activeTaskForRunLine]);

  React.useEffect(() => {
    const wasPending = wasPendingRef.current;
    wasPendingRef.current = isPending;

    // Only live pending -> completed transitions represent a fresh write.
    // Completed history can re-render long after the file changed and should
    // not make open tabs look stale.
    if (!wasPending || isPending || !result || bodyIsError || !FILE_MUTATION_TOOLS.has(block.name)) return;
    const notificationKey = `${block.id}:${block.name}`;
    if (notifiedFileChangeRef.current === notificationKey) return;
    const path = codePanePathForTool(filePathForTool(displayBlock.input ?? {}), cwd);
    if (!path) return;
    if (notifyCodePaneFileChanged(path, `agent-tool:${block.id}`)) {
      notifiedFileChangeRef.current = notificationKey;
    }
  }, [block.id, block.name, bodyIsError, cwd, displayBlock.input, isPending, result]);

  function toggle() {
    if (!expandable) return;
    setExpandedOverride(!isExpanded);
  }

  function openInspectableTarget(event: React.MouseEvent<HTMLButtonElement>) {
    if (!inspectableTarget) return;
    event.preventDefault();
    event.stopPropagation();
    openCodePaneFile(inspectableTarget.path, 'agent', inspectableTarget.candidates);
  }

  // The verb is the row's bold prefix — usually the family verb
  // ("Read", "Monitor"), but a description that starts with its own
  // imperative ("Re-run the …") lifts that word into this slot so
  // the row never reads "Ran Re-run …". The leading glyph is the
  // per-tool icon so a long ledger of mixed calls is scannable at
  // a glance.
  //
  // ``accentColorClass`` is the shared accent both the leading
  // icon and the verb pull: medium gray on neutral rows so the row
  // reads as "bold ICON VERB | quiet continuation," red when the
  // tool errored, and dim gray when interrupted so state takes
  // over color before the glyph does. ``font-medium`` carries the
  // emphasis; the tint stays soft so icons and verbs don't print
  // as near-black against the muted column.
  const verb = summary.primaryVerb ?? family.verb;
  const LeadingIcon = family.icon;
  const hasMutedContinuation = Boolean(summary.prose) && !isError && !isInterrupted;
  const accentColorClass = isError
    ? 'text-red-700 dark:text-red-300'
    : isInterrupted
      ? 'text-gray-500 dark:text-gray-400'
      : 'text-gray-600 dark:text-gray-300';
  const subagentType = subagentTypeText(displayBlock.input ?? {}, subagent);
  const subagentDetail = subagentDetailText(subagent);

  return (
    <div data-agent-tool-use-id={block.id} className={cn('group/tool text-[14px]', className)}>
      {/* Inline-flex content-width row, NOT a full-width card.
          Hovering reveals a faint neutral bleed; the chevron rotates
          open on click. The whole row inherits the column's font
          size / leading so it sits in the same rhythm as agent
          prose; it's only the dim color that sets it apart. */}
      <div
        onClick={expandable ? toggle : undefined}
        className={cn(
          '-mx-1 inline-flex max-w-full select-text items-center gap-1 overflow-hidden rounded px-1 py-px text-left transition-colors',
          expandable && 'cursor-pointer',
          expandable && 'hover:bg-gray-100/70 dark:hover:bg-white/[0.04]',
          isError
            ? 'text-red-700 dark:text-red-300'
            : isInterrupted
              ? 'text-gray-500 dark:text-gray-400'
              : 'text-gray-600 dark:text-gray-300'
        )}
      >
        {/* All inline text (verb + primary + prose) collapses into a
            SINGLE flex-item span with real whitespace between the
            styled inner runs. Keeping it as one item is what makes
            the row copy as one line — the browser blockifies every
            flex item, so splitting verb/primary/prose into separate
            items dropped a newline between every word in the
            clipboard. The row stays single-line so long commands and
            event descriptions truncate inside the assistant column;
            trailing badges / spinner / chevron stay separate as
            non-text fixtures and are unaffected by selection. */}
        <span
          className={cn('min-w-0 select-text truncate', hasMutedContinuation && 'text-gray-400 dark:text-gray-500')}
        >
          {/* Leading glyph for the tool. ``aria-hidden`` +
              ``select-none`` keep it out of the row's selection /
              copy / screen-reader text so the row still copies as
              prose. The glyph wears the same ``accentColorClass``
              as the verb so they read as one unit; default Lucide
              stroke (2) matches ``font-medium``'s optical weight
              so neither over- nor under-shoots the verb. */}
          <LeadingIcon
            className={cn('mr-1 inline-block size-3 shrink-0 select-none align-[-0.05em]', accentColorClass)}
            aria-hidden
          />
          {/* Verb is the row's bold accent. ``font-medium`` plus
              ``accentColorClass`` print medium gray on neutral
              rows, red on errors, and dim on interrupts -- the
              surrounding primary / continuation stay at row
              default (gray-600), so the row visually parses as
              "BOLD ACTION quiet metadata." */}
          <span className={cn('font-medium', accentColorClass)}>{verb}</span>
          {summary.primary && (
            <>
              {' '}
              {inspectableTarget ? (
                <button
                  type="button"
                  aria-label={`Open ${summary.primary} in Code Pane`}
                  className={cn(
                    'inline cursor-pointer rounded-sm p-0 underline decoration-gray-300/80 underline-offset-2 transition-[color,text-decoration-color] duration-150 dark:decoration-gray-600/80',
                    summary.primaryMono ? 'font-mono text-[13px]' : 'text-[14px]',
                    'text-gray-700 hover:text-gray-900 hover:decoration-gray-500 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300',
                    'dark:text-gray-200 dark:focus-visible:ring-white/20 dark:hover:text-gray-100 dark:hover:decoration-gray-400',
                    summary.primaryClassName
                  )}
                  onClick={openInspectableTarget}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {summary.primary}
                </button>
              ) : summary.primaryMono ? (
                <span
                  className={cn(
                    'font-mono text-[13px]',
                    hasMutedContinuation && 'text-gray-600 dark:text-gray-300',
                    summary.primaryClassName
                  )}
                >
                  {summary.primary}
                </span>
              ) : summary.primaryClassName ? (
                <span className={summary.primaryClassName}>{summary.primary}</span>
              ) : (
                <span className={cn(hasMutedContinuation && 'text-gray-600 dark:text-gray-300')}>
                  {summary.primary}
                </span>
              )}
            </>
          )}
          {summary.prose && (
            <>
              {' '}
              <span className="text-gray-400 dark:text-gray-500">{summary.prose}</span>
            </>
          )}
        </span>
        {(summary.badges || isPending) && (
          <span className="relative top-px flex shrink-0 select-none items-center gap-1">
            {summary.badges && (
              <span className="inline-flex shrink-0 select-none items-center" aria-hidden>
                {summary.badges}
              </span>
            )}
            {/* Trailing pending spinner — kept out of selection /
                screen-reader text so copied chat reads as prose only. */}
            {isPending && (
              <Spinner
                size="size-3"
                className={isError ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}
              />
            )}
          </span>
        )}
        {(bodyIsError || expandable) && (
          <span className="relative top-px -ml-0.5 inline-grid shrink-0 select-none auto-cols-[0.9rem] grid-flow-col items-center gap-0 leading-none">
            {bodyIsError && (
              <SimpleTooltip
                content={<span className="whitespace-pre-wrap">{errorMessage ?? 'Tool failed'}</span>}
                className="max-h-64 max-w-[28rem] overflow-auto"
              >
                <span
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center',
                    isInterrupted ? 'text-gray-400 dark:text-gray-500' : 'text-red-600 dark:text-red-300'
                  )}
                >
                  <AlertCircle className="size-3" aria-hidden />
                  <span className="sr-only">
                    {isInterrupted ? 'Tool stopped' : 'Tool failed'}: {errorMessage ?? 'unknown error'}
                  </span>
                </span>
              </SimpleTooltip>
            )}
            {expandable && (
              <button
                type="button"
                aria-label={isExpanded ? 'Collapse tool details' : 'Expand tool details'}
                aria-expanded={isExpanded}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggle();
                }}
                className={cn(
                  'inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm text-gray-400 transition-[background-color,opacity] dark:text-gray-500',
                  'hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20 dark:hover:bg-white/[0.08] dark:hover:text-gray-300',
                  isExpanded ? 'opacity-100' : 'opacity-70 group-hover/tool:opacity-100'
                )}
              >
                {isExpanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
              </button>
            )}
          </span>
        )}
      </div>

      {/* Subagent run line — neutral, transcript-local status for
          delegated work. Multiple Task / Agent tool calls naturally
          stack here under the assistant event that spawned them, which
          mirrors Claude Code's agent panel without moving the state to
          the global input rail. */}
      {!activeTaskForRunLine && isSubagentTool && isLiveSubagentLine(subagent, isPending) ? (
        <SubagentRunLine
          activeTask={activeTask}
          subagent={subagent}
          typeLabel={subagentType}
          fallbackDetail={subagentDetail}
        />
      ) : null}

      {/* Expanded body — flush under the row and indented so the
          association reads visually, but capped at ~80% of the chat
          column so it never spans corner-to-corner. The wrapper is
          intentionally chrome-free: each ``Section`` carries its
          own subtle tint, and only an errored tool gets a quiet red
          rule to flag it.

          Collapsed rows keep the body mounted for the close transition,
          then drop it after the animation finishes so large Read / Bash /
          Grep payloads don't stay resident in the DOM. */}
      {expandable && (
        <div
          aria-hidden={!isExpanded}
          className={cn(
            'grid transition-all duration-200 ease-out',
            isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <div
              className={cn(
                'mt-1.5 ml-2 flex max-w-[680px] flex-col gap-2',
                isError && 'border-red-300/70 border-l-2 pl-3 dark:border-red-500/40'
              )}
            >
              {activeTaskForRunLine && (
                <TaskRunLine
                  activeTask={activeTaskForRunLine}
                  cwd={cwd}
                  sessionId={sessionId ?? null}
                  isRunning={activeTaskIsRunning}
                  onStopTask={onStopTask}
                  outputReader={taskOutputReader}
                  pollIntervalMs={taskOutputPollIntervalMs}
                  command={commandForTaskTail(displayBlock.input ?? {})}
                  subagent={subagent}
                  typeLabel={taskTypeLabel(activeTaskForRunLine, subagentType)}
                  fallbackDetail={subagentDetail}
                />
              )}
              {children}
              {mediaPieces.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {mediaPieces.map((piece, i) => (
                    <MediaThumb key={i} piece={piece} preferredPathCandidates={inspectableTarget?.candidates} />
                  ))}
                </div>
              )}
              {renderedBodyContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export function isToolUseRowPending(
  activeTaskForRunLine: ActiveTask | undefined,
  pending: boolean | undefined,
  subagent: SubagentState | undefined,
  result: ToolResultBlock | undefined
): boolean {
  return activeTaskForRunLine !== undefined || (pending ?? (subagent ? subagent.status === 'running' : !result));
}

export function shouldDefaultExpandToolUseRow({
  taskDefaultExpanded,
  bodyIsError,
  hasImageMedia,
  toolName,
}: {
  taskDefaultExpanded: boolean;
  bodyIsError: boolean;
  hasImageMedia: boolean;
  toolName: string;
}): boolean {
  return taskDefaultExpanded || (!bodyIsError && hasImageMedia && isReadTool(toolName));
}

function inspectableCodeTarget(
  path: string | undefined,
  cwd: string | null
): { path: string; candidates: string[] } | null {
  const candidates = codePanePathCandidates(path, cwd);
  const normalized = candidates[0];
  if (!normalized) return null;
  return { path: normalized, candidates };
}

function codePanePathForTool(path: string | undefined, cwd: string | null): string | null {
  return codePanePathCandidates(path, cwd)[0] ?? null;
}

function isReadTool(name: string): boolean {
  return name === 'Read' || name === 'ReadFile';
}

function TaskRunLine({
  activeTask,
  cwd,
  sessionId,
  isRunning,
  onStopTask,
  outputReader,
  pollIntervalMs,
  command,
  subagent,
  typeLabel,
  fallbackDetail,
}: {
  activeTask: ActiveTask;
  cwd: string | null;
  sessionId: string | null;
  isRunning: boolean;
  onStopTask?: (taskId: string) => void;
  outputReader?: TaskOutputReader;
  pollIntervalMs?: number;
  command?: string | null;
  subagent?: SubagentState;
  typeLabel: string | null;
  fallbackDetail: string | null;
}) {
  const status = subagent?.status ?? (isRunning ? 'running' : 'completed');
  const activity = fallbackDetail ?? activeTask.description ?? 'Background task';
  const stats = subagentStats(subagent, activeTask);

  return (
    <div
      className={cn(
        'mt-0.5 ml-2 flex min-w-0 max-w-full flex-col gap-1.5 overflow-hidden rounded-lg border px-2.5 py-2 text-[12px] leading-5',
        'border-gray-200/80 bg-gray-50/80 text-gray-600 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-400'
      )}
      title={[statusLabel(status), typeLabel, activity, ...stats].filter(Boolean).join(' · ')}
    >
      <div className="flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap">
        <span
          className={cn(
            'inline-flex h-5 shrink-0 items-center gap-1.5 rounded-full border px-2 font-medium text-[10.5px] leading-5',
            statusBadgeClass(status)
          )}
        >
          <span className={cn('size-1.5 shrink-0 rounded-full', statusDotClass(status))} aria-hidden />
          {statusLabel(status)}
        </span>
        {typeLabel && (
          <span className="shrink-0 font-medium text-[10.5px] text-gray-400 uppercase tracking-[0.09em] dark:text-gray-500">
            {typeLabel.toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate font-mono text-gray-700 dark:text-gray-300">{activity}</span>
        {stats.length > 0 && <span className="shrink-0 text-gray-400 dark:text-gray-500">{stats.join(' · ')}</span>}
        {isRunning && onStopTask && (
          <button
            type="button"
            onClick={() => onStopTask(activeTask.taskId)}
            className={cn(
              'inline-flex h-6 shrink-0 cursor-pointer select-none items-center gap-1 rounded-md border px-2 font-medium text-[11px] leading-none transition-colors',
              'border-gray-200 text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/30',
              'dark:border-white/10 dark:text-gray-300 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-300'
            )}
          >
            <Square className="size-3" />
            <span>{taskStopLabel(activeTask)}</span>
          </button>
        )}
      </div>
      <TaskTailView
        task={activeTask}
        cwd={cwd}
        sessionId={sessionId}
        isRunning={isRunning}
        command={command}
        outputReader={outputReader}
        pollIntervalMs={pollIntervalMs}
      />
    </div>
  );
}

function SubagentRunLine({
  activeTask,
  subagent,
  typeLabel,
  fallbackDetail,
}: {
  activeTask?: ActiveTask;
  subagent?: SubagentState;
  typeLabel: string | null;
  fallbackDetail: string | null;
}) {
  const status = subagent?.status ?? 'starting';
  const activity = fallbackDetail ?? activeTask?.description ?? 'Starting subagent';
  const stats = subagentStats(subagent, activeTask);
  return (
    <div
      className={cn(
        'mt-1.5 ml-2 flex min-w-0 max-w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-[12px] leading-5',
        'border-gray-200/80 bg-gray-50/80 text-gray-600 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-400'
      )}
      title={[statusLabel(status), typeLabel, activity, ...stats].filter(Boolean).join(' · ')}
    >
      <span
        className={cn(
          'inline-flex h-5 shrink-0 items-center gap-1.5 rounded-full border px-2 font-medium text-[10.5px] leading-5',
          statusBadgeClass(status)
        )}
      >
        <span className={cn('size-1.5 shrink-0 rounded-full', statusDotClass(status))} aria-hidden />
        {statusLabel(status)}
      </span>
      {typeLabel && (
        <span className="shrink-0 font-medium text-[10.5px] text-gray-400 uppercase tracking-[0.09em] dark:text-gray-500">
          {typeLabel.toUpperCase()}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate font-mono text-gray-700 dark:text-gray-300">{activity}</span>
      {stats.length > 0 && <span className="shrink-0 text-gray-400 dark:text-gray-500">{stats.join(' · ')}</span>}
    </div>
  );
}

function statusLabel(status: SubagentState['status'] | 'starting'): string {
  if (status === 'completed') return 'Done';
  return status[0].toUpperCase() + status.slice(1);
}

function statusBadgeClass(status: SubagentState['status'] | 'starting'): string {
  switch (status) {
    case 'running':
      return 'border-gray-200 bg-white text-gray-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-300';
    case 'completed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-200';
    case 'failed':
      return 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200';
    case 'stopped':
      return 'border-gray-200 bg-white text-gray-500 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-400';
    case 'starting':
      return 'border-gray-200 bg-white text-gray-500 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-400';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function statusDotClass(status: SubagentState['status'] | 'starting'): string {
  switch (status) {
    case 'running':
      return 'bg-gray-500 motion-safe:animate-pulse dark:bg-gray-300';
    case 'completed':
      return 'bg-emerald-500 dark:bg-emerald-400';
    case 'failed':
      return 'bg-red-500 dark:bg-red-400';
    case 'stopped':
      return 'bg-gray-400 dark:bg-gray-500';
    case 'starting':
      return 'bg-gray-300 motion-safe:animate-pulse dark:bg-gray-500';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function subagentStats(subagent: SubagentState | undefined, activeTask: ActiveTask | undefined): string[] {
  const usage = subagent?.usage ?? activeTask?.usage;
  const stats: string[] = [];
  if (usage?.tool_uses !== undefined) {
    const noun = usage.tool_uses === 1 ? 'tool' : 'tools';
    stats.push(`${usage.tool_uses} ${noun}`);
  }
  return stats;
}

function isLiveSubagentLine(subagent: SubagentState | undefined, isPending: boolean): boolean {
  return isPending || subagent?.status === 'running';
}

function subagentDetailText(subagent: SubagentState | undefined): string | null {
  if (!subagent) return null;
  const terminalSummary = subagent.summary?.trim();
  const description = subagent.description?.trim();
  if (subagent.status === 'completed' || subagent.status === 'failed' || subagent.status === 'stopped') {
    return terminalSummary || description || null;
  }
  return description || null;
}

function subagentTypeText(input: Record<string, unknown>, subagent: SubagentState | undefined): string | null {
  const raw = typeof input.subagent_type === 'string' ? input.subagent_type : subagent?.taskType;
  const trimmed = raw?.trim();
  return trimmed ? cleanSubagentTypeLabel(trimmed) : null;
}

function cleanSubagentTypeLabel(value: string): string {
  const label = formatSubagentType(value).toLowerCase();
  if (label === 'local agent') return 'Subagent';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function taskTypeLabel(activeTask: ActiveTask, subagentType: string | null): string {
  if (activeTask.category === 'agent') return subagentType ?? 'Subagent';
  if (activeTask.category === 'bash') return 'Background shell';
  if (activeTask.category === 'monitor') return 'Monitor';
  return 'Task';
}

function taskStopLabel(activeTask: ActiveTask): string {
  return activeTask.category === 'bash' ? 'Kill' : 'Stop';
}

function commandForTaskTail(input: Record<string, unknown>): string | null {
  const command = input.command ?? input.cmd;
  return typeof command === 'string' && command.trim().length > 0 ? command : null;
}

function taskRunLineActiveTask(
  activeTask: ActiveTask | undefined,
  input: Record<string, unknown>
): ActiveTask | undefined {
  if (!activeTask) return undefined;
  if (activeTask.category === 'agent' || activeTask.category === 'monitor') return activeTask;
  if (activeTask.category === 'bash' && input.run_in_background === true) return activeTask;
  return undefined;
}

function buildPendingToolSummary(block: ToolUseBlock, cwd: string | null): ReturnType<typeof buildToolSummary> {
  const input = block.input ?? {};
  const path = pendingPath(input);
  const description = typeof input.description === 'string' ? input.description : null;
  const command = typeof input.command === 'string' ? input.command : typeof input.cmd === 'string' ? input.cmd : null;

  if (description || command || path) {
    return buildToolSummary(block, cwd);
  }

  switch (block.name) {
    case 'Write':
      return { primaryVerb: 'Preparing', primary: 'write', primaryMono: false };
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
      return { primaryVerb: 'Preparing', primary: 'edit', primaryMono: false };
    case 'Read':
      return { primaryVerb: 'Preparing', primary: 'read', primaryMono: false };
    case 'Bash':
    case 'bash':
      return { primaryVerb: 'Preparing', primary: 'command', primaryMono: false };
    case 'Monitor':
      return { primaryVerb: 'Preparing', primary: 'monitor', primaryMono: false };
    case 'Task':
    case 'Agent':
      return { primaryVerb: 'Preparing', primary: 'subagent', primaryMono: false };
    case 'TaskList':
      return { primaryVerb: 'Listing', primary: 'tasks', primaryMono: false };
    default: {
      // MCP tools arrive as ``mcp__<server>__<tool>``; the raw form is
      // unreadable in chat prose ("Preparing
      // mcp__antioch-jupyter__list_kernels"). Split into the tool name
      // in the primary slot and the server as a quiet "via …" prose
      // continuation so the row reads as a sentence.
      const humanized = humanizeToolName(block.name);
      if (humanized.isMcp) {
        return {
          primaryVerb: 'Preparing',
          primary: humanized.display,
          primaryMono: false,
          prose: `via ${humanized.server}`,
        };
      }
      return { primaryVerb: 'Preparing', primary: block.name || 'tool call', primaryMono: false };
    }
  }
}

function inputFromActiveTask(activeTask: ActiveTask | undefined): Record<string, unknown> {
  if (!activeTask?.description) return {};
  return { description: activeTask.description };
}

function pendingPath(input: Record<string, unknown>): string | null {
  for (const key of ['file_path', 'notebook_path', 'target_notebook', 'path']) {
    const value = input[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

// Tool input streams in as a JSON buffer that grows with every delta.
// Only the small leading fields (file_path, description, command) feed
// the collapsed streaming row -- the full body is never rendered until
// the tool finalizes and ``block.input`` takes over. Scanning the whole
// buffer on every delta is O(n) per delta and O(n^2) across a large
// file write (a multi-MB ``content`` / ``new_string``), which stalls the
// main thread -- and the pending spinner that rides it -- for the entire
// write. Bound the scan to the head so the per-delta cost stays flat
// regardless of file size.
const PARTIAL_INPUT_SCAN_LIMIT = 16384;

function readPartialToolInput(partialJson: string | undefined): Record<string, unknown> {
  if (!partialJson) return {};
  if (partialJson.length > PARTIAL_INPUT_SCAN_LIMIT) {
    return readPartialStringFields(partialJson.slice(0, PARTIAL_INPUT_SCAN_LIMIT));
  }
  try {
    const parsed: unknown = JSON.parse(partialJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return readPartialStringFields(partialJson);
  }
}

function readPartialStringFields(partialJson: string): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  for (const key of [
    'description',
    'command',
    'cmd',
    'file_path',
    'notebook_path',
    'target_notebook',
    'path',
    'pattern',
    'query',
    'url',
  ]) {
    const value = readPartialJsonString(partialJson, key);
    if (value !== null) fields[key] = value;
  }
  return fields;
}

function readPartialJsonString(partialJson: string, key: string): string | null {
  const keyIndex = partialJson.indexOf(`"${key}"`);
  if (keyIndex < 0) return null;
  const colonIndex = partialJson.indexOf(':', keyIndex);
  if (colonIndex < 0) return null;
  const quoteIndex = partialJson.indexOf('"', colonIndex);
  if (quoteIndex < 0) return null;

  let escaped = false;
  let value = '';
  for (let index = quoteIndex + 1; index < partialJson.length; index++) {
    const char = partialJson[index];
    if (escaped) {
      value += char;
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '"') return value;
    value += char;
  }
  return value.length > 0 ? value : null;
}
